<script setup lang="ts">
import { ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import AppImage from '@uiKit/elements/Image.vue'
import AppButton from '@uiKit/elements/Button.vue'
import xrpLedgerLogo from '@assets/images/xprledger.jpg'

const COPY_FEEDBACK_MS = 1600

interface Props {
  /** Public explorer URL for this investor's XRPL account. */
  explorerUrl?: string
}

const props = withDefaults(defineProps<Props>(), {
  explorerUrl: '',
})

const copied = ref(false)
let copyTimer: ReturnType<typeof setTimeout> | null = null

const onCopy = async () => {
  if (!props.explorerUrl) return
  try {
    await navigator.clipboard.writeText(props.explorerUrl)
    copied.value = true
    if (copyTimer) clearTimeout(copyTimer)
    copyTimer = setTimeout(() => {
      copied.value = false
    }, COPY_FEEDBACK_MS)
  } catch {
    // clipboard unavailable — silent, the Visit Link button still works
  }
}

const onVisit = () => {
  if (!props.explorerUrl) return
  window.open(props.explorerUrl, '_blank', 'noopener,noreferrer')
}
</script>

<template>
  <Box align="center" justify="between" gap="6" is="section" class="xlc">
    <Box direction="col" gap="1" grow class="xlc__text">
      <!-- Per the design the brand mark is artwork and only "Wallet" is type. -->
      <Box align="center" gap="1" is="h2" class="xlc__title">
        <AppImage :src="xrpLedgerLogo" alt="XRP Ledger" class="xlc__logo" />
        <Text type="heading" weight="semibold" is="span">Wallet</Text>
      </Box>
      <Text type="body" color="muted" is="p">
        View all your transactions and tokens directly on XRP Ledger
      </Text>
      <!-- Never leave a disabled money-adjacent action unexplained (ux §2). -->
      <Text v-if="!explorerUrl" type="caption" color="muted" is="p">
        Your ledger address becomes available once your token wallet is set up.
      </Text>
    </Box>

    <Box align="center" gap="3" class="xlc__actions">
      <AppButton
        :label="copied ? 'Copied' : 'Copy Link'"
        :icon="copied ? 'check' : 'copy'"
        color="secondary"
        variant="outline"
        :disabled="!explorerUrl"
        class="xlc__btn"
        @click="onCopy"
      />
      <AppButton
        label="Visit Link"
        icon="external-link"
        :disabled="!explorerUrl"
        class="xlc__btn"
        @click="onVisit"
      />
    </Box>
  </Box>
</template>

<style scoped>
.xlc {
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: 16px;
  padding: 1.5rem 1.75rem;
  box-sizing: border-box;
}

.xlc__text {
  min-width: 0;
}

.xlc__title {
  margin: 0;
}

/* Wordmark ships at its native 110×28. */
.xlc__logo {
  width: 110px;
  height: 28px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}

/* The supplied mark is a JPEG, so it carries an opaque white plate. Giving it
   a white chip keeps the edges intentional on any surface (notably dark mode)
   rather than looking like a stray rectangle. Drop this once a transparent
   SVG replaces the JPEG. */
.xlc__logo {
  background: #ffffff;
  border-radius: 4px;
}

.xlc__actions {
  flex-shrink: 0;
}

@media (max-width: 992px) {
  .xlc {
    flex-direction: column;
    align-items: stretch;
    padding: 1.25rem 1rem;
  }

  .xlc__actions {
    margin-top: 0.25rem;
  }

  .xlc__btn {
    flex: 1;
  }
}
</style>
